import { Button, Icons, Input, Label, SecretInput, Spinner } from '@repo/ui'
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from '@tanstack/react-router'
import { type FormEvent, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { AuthShell } from '@/components/auth/auth-shell'
import { useAuth } from '@/hooks/use-auth'
import { authApi } from '@/lib/api'
import type { Membership } from '@/lib/api-types'
import { signInWithGoogle } from '@/lib/social-auth'

export const Route = createFileRoute('/entrar')({
  validateSearch: (search: Record<string, unknown>) => ({
    returnTo: typeof search.returnTo === 'string' ? search.returnTo : undefined,
  }),
  component: LoginPage,
})

function LoginPage() {
  const navigate = useNavigate()
  const { returnTo } = useSearch({ from: '/entrar' })
  const { refresh, user, loading, currentOrgId, memberships } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  // Quem tem segundo fator não recebe sessão no primeiro POST: o servidor
  // responde `twoFactorRedirect` e a sessão só sai depois do código.
  const [awaitingSecondFactor, setAwaitingSecondFactor] = useState(false)
  const [code, setCode] = useState('')
  const [usingBackupCode, setUsingBackupCode] = useState(false)

  async function handleGoogleLogin() {
    setGoogleLoading(true)
    try {
      const target = getSafeReturnTo(true)
      await signInWithGoogle(`${window.location.origin}${target}`)
    } catch {
      toast.error('Não foi possível iniciar o login com Google.')
      setGoogleLoading(false)
    }
  }

  async function fetchMembershipsAfterAuth() {
    try {
      const { data } = await authApi.get<Membership[]>(
        '/v1/users/me/memberships'
      )
      return data ?? []
    } catch {
      return []
    }
  }

  function getSafeReturnTo(hasOrganization: boolean) {
    if (!hasOrganization) return '/sem-organizacao'
    if (!returnTo) return '/inbox'

    let candidate = returnTo
    for (let i = 0; i < 5; i += 1) {
      try {
        const decoded = decodeURIComponent(candidate)
        if (decoded === candidate) break
        candidate = decoded
      } catch {
        break
      }
    }

    if (!candidate.startsWith('/')) return '/inbox'

    try {
      const url = new URL(candidate, window.location.origin)
      if (url.pathname === '/entrar') return '/inbox'
      return `${url.pathname}${url.search}${url.hash}`
    } catch {
      return '/inbox'
    }
  }

  async function navigateAfterLogin(hasOrganization: boolean) {
    const target = getSafeReturnTo(hasOrganization)
    await navigate({ to: target as never })
  }

  useEffect(() => {
    if (!loading && user) {
      void navigateAfterLogin(!!currentOrgId || memberships.length > 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrgId, loading, memberships.length, user])

  async function completeLogin() {
    await refresh()
    const nextMemberships = await fetchMembershipsAfterAuth()
    toast.success('Bem-vindo de volta!')
    await navigateAfterLogin(nextMemberships.length > 0)
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setSubmitting(true)

    try {
      const { data } = await authApi.post<{ twoFactorRedirect?: boolean }>(
        '/api/auth/sign-in/email',
        { email, password, rememberMe: true }
      )

      // Sem isto a tela trataria a resposta como sucesso, não acharia sessão e
      // devolveria o usuário ao login — quem ativasse MFA ficaria preso.
      if (data?.twoFactorRedirect) {
        setAwaitingSecondFactor(true)
        setSubmitting(false)
        return
      }

      await completeLogin()
    } catch {
      setSubmitting(false)
      return
    }

    setSubmitting(false)
  }

  const handleSecondFactor = async (event: FormEvent) => {
    event.preventDefault()
    setSubmitting(true)

    const path = usingBackupCode
      ? '/api/auth/two-factor/verify-backup-code'
      : '/api/auth/two-factor/verify-totp'

    try {
      await authApi.post(path, { code: code.trim() })
      await completeLogin()
    } catch {
      setCode('')
      setSubmitting(false)
      return
    }

    setSubmitting(false)
  }

  function cancelSecondFactor() {
    setAwaitingSecondFactor(false)
    setUsingBackupCode(false)
    setCode('')
    setPassword('')
  }

  if (awaitingSecondFactor) {
    return (
      <AuthShell
        title="Verificação em duas etapas"
        subtitle={
          usingBackupCode
            ? 'Use um dos códigos de recuperação que você guardou'
            : 'Digite o código do seu aplicativo autenticador'
        }
      >
        <form onSubmit={handleSecondFactor} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="code">
              {usingBackupCode
                ? 'Código de recuperação'
                : 'Código de 6 dígitos'}
            </Label>
            <Input
              id="code"
              required
              autoFocus
              autoComplete="one-time-code"
              inputMode={usingBackupCode ? 'text' : 'numeric'}
              placeholder={usingBackupCode ? '' : '000000'}
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          </div>
          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? <Spinner /> : null}
            Verificar
          </Button>
        </form>

        <div className="mt-6 flex flex-col items-center gap-2 text-sm">
          <button
            type="button"
            className="text-primary font-medium hover:underline"
            onClick={() => {
              setUsingBackupCode((value) => !value)
              setCode('')
            }}
          >
            {usingBackupCode
              ? 'Usar o aplicativo autenticador'
              : 'Perdi o acesso ao autenticador'}
          </button>
          <button
            type="button"
            className="text-muted-foreground hover:underline"
            onClick={cancelSecondFactor}
          >
            Entrar com outra conta
          </button>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell title="Entrar" subtitle="Acesse seu SDR com IA">
      <Button
        type="button"
        variant="outline"
        className="w-full"
        disabled={googleLoading || submitting}
        onClick={() => void handleGoogleLogin()}
      >
        {googleLoading ? <Spinner /> : <Icons.google />}
        Entrar com Google
      </Button>

      <div className="my-6 flex items-center gap-3">
        <div className="bg-border h-px flex-1" />
        <span className="text-muted-foreground text-xs">ou</span>
        <div className="bg-border h-px flex-1" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-4">
            <Label htmlFor="password">Senha</Label>
            <Link
              to="/esqueci-senha"
              className="text-primary text-xs font-medium hover:underline"
            >
              Esqueci minha senha
            </Link>
          </div>
          <SecretInput
            id="password"
            visibilityLabel="senha"
            required
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>
        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? <Spinner /> : null}
          Entrar
        </Button>
      </form>
      <p className="text-muted-foreground mt-6 text-center text-sm">
        Ainda não tem conta?{' '}
        <Link
          to="/cadastrar"
          className="text-primary font-medium hover:underline"
        >
          Criar conta
        </Link>
      </p>
    </AuthShell>
  )
}
